import { ConfigProvider, Dropdown, MenuProps, Upload } from 'antd';
import { MailOutlined, CopyOutlined } from '@ant-design/icons';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import { HiOutlineChevronDown } from 'react-icons/hi';
import * as Icon from 'base/ui/components/Icons';
import CustomButton from 'base/ui/components/Button';
import { ExportButton } from 'pages/Components/FunctionButton';
import { BASE_API_URL } from 'utils/constants';
import { getAccessToken } from 'utils/localStorageHelper';
import { useTranslation } from 'react-i18next';
import { MdOutlinePostAdd } from 'react-icons/md';

/**
 * Props cho component các nút action
 */
interface ActionButtonsProps {
  isCreate: boolean;
  isCreateVote: boolean;
  isExport: boolean;
  isExportTemplate: boolean;
  isImport: boolean;
  isSendEmail?: boolean;
  exportLoading: boolean;
  exportTemplateLoading: boolean;
  exportEditTemplateLoading: boolean;
  importLoading: boolean;
  sendEmailLoading: boolean;
  onExportList: () => void;
  onExportTemplate: () => void;
  onExportTemplateMenuClick: MenuProps['onClick'];
  onImportChange: (info: UploadChangeParam<UploadFile<any>>) => void;
  onSendEmail: () => void;
  onShowFilter: () => void;
  onCreateVoucher: () => void;
  createVoucherDisabled: boolean;

  setOpenXacNhanImport?: (open: boolean) => void;

  // Sao chép nhiều kế hoạch
  onOpenMultiCopy: () => void;
  isCopyMode?: boolean;
  onConfirmCopyMode?: () => void;
  onCancelCopyMode?: () => void;
  canConfirmCopyMode?: boolean;
}

/**
 * Component chứa các nút action (Thêm mới, Export, Import, Gửi mail, Filter)
 */
export const ActionButtons = ({
  isCreate,
  isCreateVote,
  isExport,
  isExportTemplate,
  isImport,
  isSendEmail,
  exportLoading,
  exportTemplateLoading,
  exportEditTemplateLoading,
  importLoading,
  sendEmailLoading,
  onExportList,
  onExportTemplate,
  onExportTemplateMenuClick,
  onImportChange,
  onSendEmail,
  onShowFilter,
  onCreateVoucher,
  createVoucherDisabled,

  setOpenXacNhanImport,
  onOpenMultiCopy,
  isCopyMode,
  onConfirmCopyMode,
  onCancelCopyMode,
  canConfirmCopyMode,
}: ActionButtonsProps) => {
  const { t } = useTranslation();

  const exportTemplateMenuItems: MenuProps['items'] = [
    {
      key: 'create-template',
      label: t('btn.export-template-create'),
      disabled: exportTemplateLoading,
    },
    {
      key: 'edit-template',
      label: t('btn.export-template-edit'),
      disabled: exportEditTemplateLoading,
    },
  ];

  return (
    <>
      {/* Nút Thêm mới */}
      {isCreate && (
        <CustomButton
          type="primary"
          icon={<Icon.Plus className="!fill-white" />}
          disabled={isCopyMode}
          onClick={() => (window.location.href = '/WasteHandoverPlan/Create')}
        >
          {t('btn.add')}
        </CustomButton>
      )}

      {/* Nút Export */}
      {isExport && (
        <ExportButton
          className="!ml-2"
          loading={exportLoading}
          disabled={isCopyMode}
          onClick={onExportList}
        />
      )}

      {/* Nút Export Template với dropdown (click bất kỳ đâu để xổ menu) */}
      {isExportTemplate && (
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#f59e0b',
            },
          }}
        >
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            disabled={isCopyMode}
            menu={{
              items: exportTemplateMenuItems,
              onClick: ({ key }) => {
                if (key === 'create-template') {
                  onExportTemplate();
                } else {
                  onExportTemplateMenuClick?.({ key } as any);
                }
              },
              style: {
                backgroundColor: '#fef3c7',
                borderRadius: 4,
              },
              className: '!py-1 export-template-dropdown',
            }}
          >
            <CustomButton
              type="primary"
              color="#f59e0b"
              loading={exportTemplateLoading || exportEditTemplateLoading}
              disabled={isCopyMode}
              className="!ml-2 !px-3 flex items-center gap-1"
            >
              <Icon.Download className="!fill-white" />
              <span>{t('btn.export-template')}</span>
              <HiOutlineChevronDown className="text-white" />
            </CustomButton>
          </Dropdown>
        </ConfigProvider>
      )}

      {/* Nút Import */}
      {isImport && (
        // <Upload
        //     maxCount={1}
        //     accept=".xls,.xlsx"
        //     showUploadList={false}
        //     className="!ml-2"
        //     action={`${BASE_API_URL}/wastehandoverplan/import`}
        //     headers={{
        //         Authorization: `Bearer ${getAccessToken()}`,
        //     }}
        //     name="File"
        //     onChange={onImportChange}
        // >
        //     <CustomButton
        //         type="primary"
        //         color="#3b82f6"
        //         loading={importLoading}
        //         icon={<Icon.Upload className="!fill-white" />}
        //     >
        //         {t('btn.import')}
        //     </CustomButton>
        // </Upload>
        <CustomButton
          type="primary"
          color="#3b82f6"
          loading={importLoading}
          disabled={isCopyMode}
          onClick={() => {
            setOpenXacNhanImport && setOpenXacNhanImport(true)
          }}
          icon={<Icon.Upload className="!fill-white" />}
        >
          {t('btn.import')}
        </CustomButton>
      )}

      {/* Nút Gửi mail */}
      {isSendEmail && (
        <CustomButton
          type="primary"
          color="#8b5cf6"
          className="!ml-2"
          loading={sendEmailLoading}
          disabled={isCopyMode}
          icon={<MailOutlined className="!text-white" />}
          onClick={onSendEmail}
        >
          {t('btn.send-email')}
        </CustomButton>
      )}

      {/* Nút Sao chép nhiều kế hoạch - theo cùng phân quyền với nút Tạo mới */}
      {isCreate && !isCopyMode && (
        <CustomButton
          type="default"
          className="!ml-2"
          icon={<CopyOutlined className="text-base" />}
          onClick={onOpenMultiCopy}
        >
          Sao chép nhiều kế hoạch
        </CustomButton>
      )}

      {/* Nút Xác nhận / Hủy sao chép nhiều kế hoạch (chỉ hiển thị khi đang ở chế độ sao chép) */}
      {isCreate && isCopyMode && (
        <>
          <CustomButton
            type="primary"
            className="!ml-2"
            icon={<CopyOutlined className="text-base" />}
            onClick={onConfirmCopyMode}
            disabled={!canConfirmCopyMode}
          >
            Xác nhận
          </CustomButton>
          <CustomButton
            type="default"
            className="!ml-2"
            onClick={onCancelCopyMode}
          >
            Hủy sao chép
          </CustomButton>
        </>
      )}

      {/* Nút Tạo phiếu (luôn hiển thị, nhưng disable khi đang ở chế độ sao chép nhiều hoặc không có dòng được chọn) */}
      {isCreateVote && (
        <CustomButton
          type="default"
          className="!ml-2"
          icon={<MdOutlinePostAdd className="text-base" />}
          disabled={isCopyMode || createVoucherDisabled}
          onClick={onCreateVoucher}
        >
          Tạo phiếu
        </CustomButton>
      )}

      {/* Nút Filter */}
      <CustomButton onClick={onShowFilter} type="primary" color="#EFF4F9" className="!px-0 w-8 !ml-2">
        <Icon.Filter className="fill-primary" />
      </CustomButton>
    </>
  );
};

